import { ASK_DICTIONARY_FOR_RAW_DEFINITIONS } from 'approot/dictionary/dictionary.graphql';
import type {
  AskDictionaryForRawDefinitionsQuery,
  AskDictionaryForRawDefinitionsQueryVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { useVocabularySettingContext } from './vocabulary-setting.context';
import Bouncer from 'approot/shared/bouncer/bouncer';
import InlineServerError from 'approot/errors/inline-server-error';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import { PStyled } from 'approot/shared/styles/typography.style';
import { grey7 } from 'approot/shared/styles/colours.style';
import { ListButton } from 'approot/shared/styles/buttons/list-button';
import {
  DictionaryDefinition,
  SOURCE_AI,
} from './vocabulary-setting.constants';
import { VocabularyListButtonContent } from './vocabulary-settings.style';
import { PrimaryButton } from 'approot/shared/styles/buttons';

const VocabularySettingAskAIForNewDefinition = ({
  onAskAIForExamples,
  onCreateNewDefinition,
  goBack,
}: {
  onAskAIForExamples: (input: DictionaryDefinition) => void;
  onCreateNewDefinition: (input: DictionaryDefinition) => void;
  goBack: () => void;
}) => {
  const { word } = useVocabularySettingContext();

  const { data, loading, error } = useLoggingQuery<
    AskDictionaryForRawDefinitionsQuery,
    AskDictionaryForRawDefinitionsQueryVariables
  >(ASK_DICTIONARY_FOR_RAW_DEFINITIONS, {
    variables: {
      input: {
        word: word?.trim() || '',
      },
    },
  });

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader isBack title="Vocabulary" onClose={goBack} />
      <ActivityBuilderDrawerContent>
        {loading ? (
          <Bouncer />
        ) : (
          <>
            {error ? (
              <Box $marginBottom={24}>
                <InlineServerError customError="Something went wrong, please go back and try again" />
              </Box>
            ) : (
              <>
                <BoxList data-selector="vocab-suggestions">
                  {data?.askDictionaryForRawDefinitions.definitions.map(
                    (item, index) => (
                      <ListButton
                        data-selector="suggested-list"
                        key={index}
                        onClick={() =>
                          onAskAIForExamples({
                            word: item.word,
                            sourceDefinition: item.definition,
                            source: SOURCE_AI,
                            lexical: item.lexical,
                          })
                        }
                      >
                        <VocabularyListButtonContent>
                          <PStyled marginBottom={4}>
                            <strong>{item.word}</strong> <i>({item.lexical})</i>
                          </PStyled>
                          <PStyled colour={grey7} marginBottom={0}>
                            {item.definition}
                          </PStyled>
                        </VocabularyListButtonContent>
                      </ListButton>
                    )
                  )}
                </BoxList>
                <Box $flexDirection="column" $marginBottom={24}>
                  <PrimaryButton
                    type="button"
                    onClick={() => {
                      onCreateNewDefinition({
                        word: word!,
                        sourceDefinition: null,
                        lexical: 'noun',
                      });
                    }}
                    data-selector="btn-create-my-word"
                  >
                    Create My Own Word
                  </PrimaryButton>
                </Box>
              </>
            )}
          </>
        )}
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default VocabularySettingAskAIForNewDefinition;
